const OUTPUT_WIDTH = 900;
const OUTPUT_HEIGHT = 240;
const HORIZONTAL_PADDING = 36;
const VERTICAL_PADDING = 18;

const loadImage = (source) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Não foi possível ler a imagem da assinatura.'));
    image.src = source;
});

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (blob) {
            resolve(blob);
        } else {
            reject(new Error('Não foi possível preparar a assinatura.'));
        }
    }, 'image/png');
});

const getPixelMetrics = (red, green, blue) => ({
    brightness: (red + green + blue) / 3,
    colorRange: Math.max(red, green, blue) - Math.min(red, green, blue),
    distanceFromWhite: (255 - red) + (255 - green) + (255 - blue),
});

const isSignatureStroke = (red, green, blue, alpha) => {
    if (alpha <= 20) return false;

    const { brightness, colorRange, distanceFromWhite } = getPixelMetrics(red, green, blue);
    return brightness < 180 || (colorRange > 18 && distanceFromWhite > 35);
};

const findInkBounds = (imageData, width, height) => {
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            const index = (y * width + x) * 4;
            const red = imageData[index];
            const green = imageData[index + 1];
            const blue = imageData[index + 2];
            const alpha = imageData[index + 3];
            if (isSignatureStroke(red, green, blue, alpha)) {
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);
            }
        }
    }

    if (maxX < minX || maxY < minY) {
        return { x: 0, y: 0, width, height };
    }

    return {
        x: minX,
        y: minY,
        width: maxX - minX + 1,
        height: maxY - minY + 1,
    };
};

export const arrayBufferToDataUrl = (arrayBuffer, contentType = 'image/png') => {
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';

    for (let index = 0; index < bytes.length; index += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
    }

    return `data:${contentType};base64,${btoa(binary)}`;
};

export const normalizeSignatureImage = async (source, sizePercent = 92) => {
    const image = await loadImage(source);
    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = image.naturalWidth || image.width;
    sourceCanvas.height = image.naturalHeight || image.height;

    const sourceContext = sourceCanvas.getContext('2d', { willReadFrequently: true });
    sourceContext.drawImage(image, 0, 0);

    const pixels = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height).data;
    const bounds = findInkBounds(pixels, sourceCanvas.width, sourceCanvas.height);

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = OUTPUT_WIDTH;
    outputCanvas.height = OUTPUT_HEIGHT;
    const outputContext = outputCanvas.getContext('2d');

    const usableWidth = OUTPUT_WIDTH - (HORIZONTAL_PADDING * 2);
    const usableHeight = OUTPUT_HEIGHT - (VERTICAL_PADDING * 2);
    const fitScale = Math.min(usableWidth / bounds.width, usableHeight / bounds.height);
    const selectedScale = fitScale * Math.min(Math.max(sizePercent, 60), 100) / 100;
    const drawWidth = bounds.width * selectedScale;
    const drawHeight = bounds.height * selectedScale;
    const drawX = (OUTPUT_WIDTH - drawWidth) / 2;
    const drawY = (OUTPUT_HEIGHT - drawHeight) / 2;

    outputContext.drawImage(
        sourceCanvas,
        bounds.x,
        bounds.y,
        bounds.width,
        bounds.height,
        drawX,
        drawY,
        drawWidth,
        drawHeight,
    );

    const outputImage = outputContext.getImageData(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    const outputPixels = outputImage.data;

    for (let index = 0; index < outputPixels.length; index += 4) {
        const red = outputPixels[index];
        const green = outputPixels[index + 1];
        const blue = outputPixels[index + 2];
        const alpha = outputPixels[index + 3];
        const { brightness, colorRange } = getPixelMetrics(red, green, blue);

        const isWhiteBackground = brightness > 245;
        const isLightNeutralBackground = brightness > 185 && colorRange < 18;

        if (alpha <= 20 || isWhiteBackground || isLightNeutralBackground) {
            outputPixels[index + 3] = 0;
        }
    }

    outputContext.putImageData(outputImage, 0, 0);

    const blob = await canvasToBlob(outputCanvas);
    return {
        blob,
        dataUrl: outputCanvas.toDataURL('image/png'),
    };
};

